import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { Loader } from '@neo/primitives/loader'
import { Check, CirclePlus, Lock, Sparkles } from 'lucide-react'
import { useRef, useState } from 'react'
import type { RAgentJudge } from 'v2/types/api/agent-observability'
import { useFlowId } from '../hooks/use-flow-id'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { type MetricDraftSeed, MetricJudgeCard } from './metrics-judge-card'
import { useAgentJudgesQuery, useGenerateMetricsMutation } from './metrics-tab.queries'
import { MetricsTestPanel } from './metrics-test-panel'

const DEFAULT_CHECKS = [
  'Hallucination',
  'Instructions adherence',
  'Intent identification',
  'Variable extraction',
  'Low engagement',
  'Node loop',
  'User sentiment',
  'Bot detection',
  'Voicemail detection',
  'Call screening',
  'Wrong number',
]

interface Draft {
  key: string
  seed?: MetricDraftSeed
}

export const MetricsTab = () => {
  const { id: agentId = '' } = useFlowId()
  const { getNodes, getEdges } = useTypedReactFlow()
  const { data: judges = [], isPending } = useAgentJudgesQuery(agentId)
  const generateMutation = useGenerateMetricsMutation()

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [testJudge, setTestJudge] = useState<RAgentJudge | null>(null)
  const [lastTest, setLastTest] = useState<Record<string, { calls: number; failed: number }>>({})
  const [calibrated, setCalibrated] = useState<Record<string, string>>({})
  const [showDefaults, setShowDefaults] = useState(false)
  const draftSeq = useRef(0)

  const nextKey = () => {
    draftSeq.current += 1
    return `draft-${draftSeq.current}`
  }

  const handleAddMetric = () => {
    const key = nextKey()
    setDrafts((prev) => [...prev, { key }])
    setExpandedId(key)
  }

  const handleGenerate = () => {
    const existing = judges.map((j) => ({
      name: j.display_name,
      description: j.description,
      scope: j.scope,
    }))
    generateMutation.mutate(
      { flow: { nodes: getNodes(), edges: getEdges() }, existing },
      {
        onSuccess: (metrics) => {
          if (metrics.length === 0) return
          const newDrafts = metrics.map((m) => ({
            key: nextKey(),
            seed: { display_name: m.display_name, description: m.description, scope: m.scope },
          }))
          setDrafts((prev) => [...prev, ...newDrafts])
          setExpandedId(newDrafts[0].key)
        },
      },
    )
  }

  const handleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id))
  const removeDraft = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key))
    setExpandedId(null)
  }

  if (isPending) return <Loader text="Loading metrics..." />

  const isEmpty = judges.length === 0 && drafts.length === 0

  return (
    <div className="flex items-start gap-6">
      <div className="flex-1 min-w-0 max-w-3xl flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-p-600">Metrics</p>
            <p className="text-s-400 text-muted-foreground">What good looks like on this agent.</p>
          </div>
          <div className="flex items-center gap-2">
            <ButtonWithLoader
              variant="outline"
              size="sm"
              loading={generateMutation.isPending}
              onClick={handleGenerate}
              className="gap-2"
              data-testid="btn-generate-metrics"
            >
              <Sparkles size={16} />
              Generate
            </ButtonWithLoader>
            <Button
              size="sm"
              onClick={handleAddMetric}
              className="gap-2"
              data-testid="btn-add-metric"
            >
              <CirclePlus size={16} />
              Add metric
            </Button>
          </div>
        </div>

        {isEmpty ? (
          <div className="bg-bg2 border border-st2 rounded-xl p-6 flex flex-col items-center gap-3">
            <p className="text-p-500">No metrics yet</p>
            <p className="text-s-400 text-secondary text-center">
              Describe what a good call looks like and every conversation gets scored against it.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddMetric}
              data-testid="btn-add-first-metric"
            >
              Add metric
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {judges.map((judge) => (
              <MetricJudgeCard
                key={judge.id}
                judge={judge}
                agentId={agentId}
                expanded={expandedId === judge.id}
                onToggleExpand={() => handleExpand(judge.id)}
                onTest={() => setTestJudge(judge)}
                lastTest={lastTest[judge.id]}
                calibratedDescription={calibrated[judge.id]}
              />
            ))}
            {drafts.map((draft) => (
              <MetricJudgeCard
                key={draft.key}
                judge={null}
                draftKey={draft.key}
                seed={draft.seed}
                agentId={agentId}
                expanded={expandedId === draft.key}
                onToggleExpand={() => handleExpand(draft.key)}
                onDraftSaved={() => removeDraft(draft.key)}
                onDiscardDraft={() => removeDraft(draft.key)}
              />
            ))}
          </div>
        )}

        <div
          className="bg-bg2 border border-st2 rounded-xl px-4 py-3 flex flex-col gap-3"
          data-testid="default-checks-band"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2.5 text-s-400 text-secondary">
              <span className="flex items-center justify-center size-6 rounded-md bg-background border border-st2 shrink-0">
                <Lock size={13} className="text-tertiary" />
              </span>
              {DEFAULT_CHECKS.length} default checks run on every call
            </span>
            <button
              type="button"
              className="text-s-400 text-primary hover:underline shrink-0"
              onClick={() => setShowDefaults((prev) => !prev)}
              data-testid="btn-view-default-checks"
            >
              {showDefaults ? 'Hide' : 'View'}
            </button>
          </div>
          {showDefaults && (
            <div
              className="flex flex-wrap gap-2 pt-3 border-t border-st2"
              data-testid="default-checks-list"
            >
              {DEFAULT_CHECKS.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1.5 rounded-full border border-st2 bg-background px-2.5 py-1 text-xs-400 text-secondary"
                >
                  <Check size={12} className="text-status-success shrink-0" />
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {testJudge && (
        <aside className="flex-1 min-w-[380px] sticky top-0 self-start">
          <MetricsTestPanel
            key={testJudge.id}
            judge={testJudge}
            agentId={agentId}
            onClose={() => setTestJudge(null)}
            onTested={(id, summary) => setLastTest((prev) => ({ ...prev, [id]: summary }))}
            onCalibrated={(id, desc) => {
              setCalibrated((prev) => ({ ...prev, [id]: desc }))
              setExpandedId(id)
            }}
          />
        </aside>
      )}
    </div>
  )
}
